import { EndsStepModel } from "../../wizard/steps/1-ends/ends-step.model";
import { TranspondersStepModel } from "../../wizard/steps/2-transponders/transponders-step.model";
import { IntermediateDevice, IntermediatesStepModel } from "../../wizard/steps/3-intermediates/intermediates-step.model";
import { LinksStepModel } from "../../wizard/steps/4-links/links-step.model";
import { AmplifiersStepModel } from "../../wizard/steps/5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "../../wizard/steps/6-osc-management/osc-management-step.model";
import { EndpointType } from "../models/diagram.models";
import {
    AmplifiersConverterData,
    EndConverterData,
    IntermediatesConverterData,
    LinksConverterData,
    MuxConverterData,
    OscsConverterData,
    TranspondersConverterData
} from "./dwdm-diagram-converter.models";

/**
 * Classe responsável por criar os modelos utilizados nos conversores do diagrama.
 */
export class DwdmDiagramConverterModelsBuilder {

    public buildAEndConverterData(endsModel: EndsStepModel, transpondersModel: TranspondersStepModel,
            intermediatesStepModel: IntermediatesStepModel, linksModel: LinksStepModel, amplifiersModel: AmplifiersStepModel,
            oscsModel: OscManagementStepModel, isOnOscStep: boolean): EndConverterData {
        return {
            end: endsModel.aEndDevice,
            slots: endsModel.aEndSlots,
            ports: endsModel.aEndPorts,
            hasMux: endsModel.aEndHasMux,
            muxDemux: endsModel.aEndMux,
            transpondersPorts: transpondersModel?.aEndPorts,
            links: linksModel?.links,
            endpointType: EndpointType.A_END,
            circuitAEnd: endsModel.aEndDevice,
            circuitZEnd: endsModel.zEndDevice,
            amplifiers: amplifiersModel?.configuredAmplifiers,
            oscs: oscsModel?.oscManagementPorts,
            allOscs: oscsModel?.allOscs,
            useOscs: oscsModel?.useOscManagement,
            isOnOscStep,
            intermediates: intermediatesStepModel?.devices
        };
    }

    public buildZEndConverterData(endsModel: EndsStepModel, transpondersModel: TranspondersStepModel,
            intermediatesStepModel: IntermediatesStepModel, linksModel: LinksStepModel, amplifiersModel: AmplifiersStepModel,
            oscsModel: OscManagementStepModel, isOnOscStep: boolean): EndConverterData {
        return {
            end: endsModel.zEndDevice,
            slots: endsModel.zEndSlots,
            ports: endsModel.zEndPorts,
            hasMux: endsModel.zEndHasMux,
            muxDemux: endsModel.zEndMux,
            transpondersPorts: transpondersModel?.zEndPorts,
            links: linksModel?.links,
            endpointType: EndpointType.Z_END,
            circuitAEnd: endsModel.aEndDevice,
            circuitZEnd: endsModel.zEndDevice,
            amplifiers: amplifiersModel?.configuredAmplifiers,
            oscs: oscsModel?.oscManagementPorts,
            allOscs: oscsModel?.allOscs,
            useOscs: oscsModel?.useOscManagement,
            isOnOscStep,
            intermediates: intermediatesStepModel?.devices
        };
    }

    public buildTranspondersConverterData(endsConverterData: EndConverterData): TranspondersConverterData {
        return {
            slots: endsConverterData.slots,
            ports: endsConverterData.ports,
            transponders: endsConverterData.transpondersPorts
        };
    }

    public buildMuxConverterData(endsConverterData: EndConverterData): MuxConverterData {
        return {
            hasMux: endsConverterData.hasMux,
            mux: endsConverterData.muxDemux
        };
    }

    public buildAmplifiersConverterData(endsConverterData: EndConverterData): AmplifiersConverterData {
        return {
            deviceName: endsConverterData.end?.name,
            deviceResourceId: endsConverterData.end?.id,
            links: endsConverterData.links,
            circuitAEnd: endsConverterData.circuitAEnd,
            circuitZEnd: endsConverterData.circuitZEnd,
            amplifiers: endsConverterData.amplifiers,
            intermediates: endsConverterData.intermediates
        };
    }

    public buildAmplifiersConverterDataForIntermediate(device: IntermediateDevice,
            intermediatesConverterData: IntermediatesConverterData): AmplifiersConverterData {
        return {
            deviceName: device.name,
            deviceResourceId: device.resourceId,
            links: intermediatesConverterData.links,
            circuitAEnd: intermediatesConverterData.circuitAEnd,
            circuitZEnd: intermediatesConverterData.circuitZEnd,
            amplifiers: intermediatesConverterData.amplifiers,
            intermediates: intermediatesConverterData.intermediates
        };
    }

    public buildIntermediatesConverterData(intermediatesModel: IntermediatesStepModel, linksModel: LinksStepModel,
            amplifiersModel: AmplifiersStepModel, endsModel: EndsStepModel, oscsModel: OscManagementStepModel,
            isOnOscStep: boolean): IntermediatesConverterData {
        return {
            hasIntermediates: intermediatesModel?.useIntermediateDevices,
            intermediates: intermediatesModel?.devices,
            links: linksModel?.links,
            circuitAEnd: endsModel.aEndDevice,
            circuitZEnd: endsModel.zEndDevice,
            amplifiers: amplifiersModel?.configuredAmplifiers,
            oscs: oscsModel?.oscManagementPorts,
            allOscs: oscsModel?.allOscs,
            useOscs: oscsModel?.useOscManagement,
            isOnOscStep
        };
    }

    public buildLinksConverterData(linksModel: LinksStepModel, isOnLinkStep: boolean) : LinksConverterData {
        return {
            links: linksModel?.links,
            isOnLinkStep
        };
    }

    public buildOscsConverterData(endConverterData: EndConverterData): OscsConverterData {
        return {
            deviceResourceId: endConverterData.end?.id,
            deviceName: endConverterData.end?.name,
            oscs: endConverterData.oscs,
            allOscs: endConverterData.allOscs,
            useOscs: endConverterData.useOscs,
            isOnOscStep: endConverterData.isOnOscStep,
            intermediates: endConverterData.intermediates
        };
    }

    public buildOscsConverterDataForIntermediate(device: IntermediateDevice, intermediatesData: IntermediatesConverterData)
            : OscsConverterData {
        return {
            deviceResourceId: device.resourceId,
            deviceName: device.name,
            oscs: intermediatesData.oscs,
            allOscs: intermediatesData.allOscs,
            useOscs: intermediatesData.useOscs,
            links: intermediatesData.links,
            circuitAEnd: intermediatesData.circuitAEnd,
            circuitZEnd: intermediatesData.circuitZEnd,
            isOnOscStep: intermediatesData.isOnOscStep,
            intermediates: intermediatesData.intermediates
        };
    }
}
